import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  Avatar,
  Flex,
  Textarea,
  Spinner,
  Stack,
  Menu,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import {
  ClockIcon,
  EllipsisHorizontalIcon,
  PaperAirplaneIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import useIsCurrentUser from '@/lib/hooks/use-is-current-user';
import api from '@/utils/api';
import { User, Comment } from '@/lib/types';

interface IComment extends Comment {
  user: User;
}

interface Props {
  contentId: number;
}

const schema = z.object({
  comment: z
    .string()
    .min(1, 'Comment cannot be empty')
    .max(500, 'Comment cannot exceed 500 characters'),
});

type formData = z.infer<typeof schema>;

export default function CommentSection({ contentId }: Props) {
  const [comments, setComments] = useState<IComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const currentUser = useIsCurrentUser();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<formData>({
    resolver: zodResolver(schema),
    defaultValues: {
      comment: '',
    },
  });

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    reset: resetEdit,
    watch: watchEdit,
    formState: { errors: errorsEdit, isSubmitting: isSubmittingEdit },
    setValue: setValueEdit,
  } = useForm<formData>({
    resolver: zodResolver(schema),
  });

  const commentValue = watch('comment');
  const isCommentEmpty = !commentValue || commentValue.trim() === '';

  const editCommentValue = watchEdit('comment');

  const fetchComments = useCallback(async () => {
    if (!contentId) return;

    setIsLoading(true);

    try {
      const response = await api.get(`/comments/workspace-content/${contentId}`);
      setComments(response.data || []);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.message || 'Failed to load comments',
      });
    } finally {
      setIsLoading(false);
    }
  }, [contentId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const onSubmit = async (data: formData) => {
    if (!currentUser) {
      return;
    }

    try {
      await api.post('/comments', {
        workspace_content_id: contentId,
        comment: data.comment,
      });

      toaster.create({
        type: 'success',
        title: 'Comment added successfully',
      });

      reset({ comment: '' });
      fetchComments();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: 'Failed to add comment',
        description: err.response?.data?.message || 'Please try again',
      });
    }
  };

  const startEditComment = (comment: IComment) => {
    setEditingCommentId(comment.id);
    setValueEdit('comment', comment.comment);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    resetEdit();
  };

  const updateComment = async (data: formData) => {
    if (!editingCommentId || !currentUser) return;

    try {
      await api.put(`/comments/${editingCommentId}`, {
        comment: data.comment,
      });

      setComments((prev) =>
        prev.map((c) => (c.id === editingCommentId ? { ...c, comment: data.comment || '' } : c)),
      );

      setEditingCommentId(null);

      toaster.create({
        type: 'success',
        title: 'Comment updated',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: 'Failed to update comment',
        description: err.response?.data?.message || 'Please try again',
      });
    }
  };

  const confirmDeleteComment = (commentId: number) => {
    setDeletingCommentId(commentId);
    setShowDeleteModal(true);
  };

  const deleteComment = async () => {
    if (!deletingCommentId || !currentUser) return;

    setIsDeleting(true);

    try {
      await api.delete(`/comments/${deletingCommentId}`);

      setComments((prev) => prev.filter((c) => c.id !== deletingCommentId));

      toaster.create({
        type: 'success',
        title: 'Comment deleted',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: 'Failed to delete comment',
        description: err.response?.data?.message || 'Please try again',
      });
    } finally {
      setIsDeleting(false);
      setDeletingCommentId(null);
      setShowDeleteModal(false);
    }
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const timeAgo = formatDistanceToNow(date, { addSuffix: true });
    const formattedDate = format(date, 'MMM d, yyyy');

    return {
      timeAgo,
      fullDate: formattedDate,
    };
  };

  const isOwnComment = (comment: IComment) => {
    return currentUser && currentUser.id === comment.user_id;
  };

  return (
    <Box mt={8} pt={6} borderTop='1px solid' borderColor='border'>
      <Text fontSize='xl' fontWeight='bold' mb={4}>
        Comments {comments.length > 0 && `(${comments.length})`}
      </Text>

      {currentUser && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={4} mb={4}>
            <Field invalid={!!errors.comment} errorText={errors.comment?.message}>
              <Textarea
                {...register('comment')}
                placeholder='Add a comment...'
                disabled={isSubmitting}
                rows={4}
                maxLength={500}
              />
              <Flex justify='flex-end' mt={1}>
                <Text
                  fontSize='xs'
                  color={
                    commentValue && commentValue.length > 400
                      ? commentValue.length > 450
                        ? 'danger'
                        : 'orange.500'
                      : 'fg.muted'
                  }
                >
                  {commentValue ? commentValue.length : 0}/500
                </Text>
              </Flex>
            </Field>

            <Flex justify='flex-end'>
              <Button
                colorPalette='brand'
                type='submit'
                loading={isSubmitting}
                disabled={isCommentEmpty || isSubmitting}
              >
                <PaperAirplaneIcon />
                Post Comment
              </Button>
            </Flex>
          </Stack>
        </form>
      )}

      {isLoading ? (
        <Flex justify='center' py={8}>
          <Spinner size='lg' color='brand.500' />
        </Flex>
      ) : comments.length === 0 ? (
        <Box textAlign='center' py={4} bg='bg.subtle' borderRadius='md'>
          <Text color='fg.muted' fontSize='xs'>
            No comments yet. Be the first to comment!
          </Text>
        </Box>
      ) : (
        <VStack gap={4} align='stretch'>
          {comments.map((comment) => {
            const { timeAgo, fullDate } = formatCommentDate(comment.created_at);
            const isEditing = editingCommentId === comment.id;

            return (
              <Box
                key={comment.id}
                p={4}
                borderWidth='1px'
                borderRadius='md'
                borderColor={'border'}
              >
                <Flex>
                  <Avatar.Root size='sm' mr={3}>
                    <Avatar.Fallback
                      name={`${comment.user.first_name} ${comment.user.last_name}`}
                    />
                  </Avatar.Root>
                  <Box flex='1'>
                    <Flex justify='space-between' align='center' mb={1}>
                      <Text fontWeight='medium'>
                        {comment.user.first_name} {comment.user.last_name}
                      </Text>
                      <Flex align='center' gap={2}>
                        <Flex align='center' fontSize='xs' color='fg.muted'>
                          <ClockIcon style={{ width: '12px', marginRight: '4px' }} />
                          <Text title={fullDate}>{timeAgo}</Text>
                        </Flex>
                        {isOwnComment(comment) && !isEditing && (
                          <Menu.Root>
                            <Menu.Trigger asChild>
                              <IconButton variant='ghost' size='xs'>
                                <EllipsisHorizontalIcon style={{ width: '16px' }} />
                              </IconButton>
                            </Menu.Trigger>
                            <Menu.Positioner>
                              <Menu.Content>
                                <Menu.Item
                                  value='edit-comment'
                                  onClick={() => startEditComment(comment)}
                                >
                                  <Icon as={PencilIcon} w={4} h={4} />
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  value='delete-comment'
                                  onClick={() => confirmDeleteComment(comment.id)}
                                >
                                  <Icon as={TrashIcon} w={4} h={4} />
                                  Delete
                                </Menu.Item>
                              </Menu.Content>
                            </Menu.Positioner>
                          </Menu.Root>
                        )}
                      </Flex>
                    </Flex>

                    {isEditing ? (
                      <form onSubmit={handleSubmitEdit(updateComment)}>
                        <Field
                          invalid={!!errorsEdit.comment}
                          errorText={errorsEdit.comment?.message}
                        >
                          <Textarea
                            {...registerEdit('comment')}
                            rows={3}
                            fontSize='xs'
                            mb={2}
                            maxLength={500}
                          />
                          <Flex justify='flex-end' mt={1} mb={2}>
                            <Text
                              fontSize='xs'
                              color={
                                editCommentValue && editCommentValue.length > 400
                                  ? editCommentValue.length > 450
                                    ? 'danger'
                                    : 'warning'
                                  : 'fg.muted'
                              }
                            >
                              {editCommentValue ? editCommentValue.length : 0}/500
                            </Text>
                          </Flex>
                        </Field>
                        <Flex justify='flex-end' gap={2}>
                          <Button size='xs' variant='ghost' onClick={cancelEditComment}>
                            Cancel
                          </Button>
                          <Button
                            size='xs'
                            colorPalette='brand'
                            type='submit'
                            loading={isSubmittingEdit}
                          >
                            Save
                          </Button>
                        </Flex>
                      </form>
                    ) : (
                      <Text whiteSpace='pre-wrap' fontSize='xs' color='fg.muted'>
                        {comment.comment}
                      </Text>
                    )}
                  </Box>
                </Flex>
              </Box>
            );
          })}
        </VStack>
      )}

      {/* Keep mounted; unmounting an open Chakra dialog leaves the body pointer/scroll lock behind. */}
      <DestructiveModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => deleteComment()}
        isLoading={isDeleting}
        title={'Delete Comment'}
        children={`Are you sure you want to delete this comment? This action cannot be undone.`}
        confirmText={'Delete'}
      />
    </Box>
  );
}
